import {
  HttpClient,
  HttpErrorResponse,
  HttpHeaders
} from '@angular/common/http';
import { Injectable, inject, signal } from '@angular/core';
import { raygun } from '@approot/app.raygun.setup';
import {
  AttributeBase,
  CalculateFutureDatePayload,
  Entity,
  GetFixingsForCurrencyPairAndCodeResponse,
  GetForwardPointsResponse,
  GetImpliedVolResponse,
  IRFixing,
  KeyValue,
  Leg,
  LegAttribute,
  LegPrice,
  MarketDataSpreadingRule,
  Payload,
  PricingData,
  ProductSpecification,
  ResultPerLeg,
  ScheduleData,
  SchedulePayload,
  Task
} from '@approot/models';
import {
  AggregateResultsResponse,
  SummedPricing
} from '@approot/models/summed-legs';
import { PhoenixService } from '@approot/services';
import { Utilities, apiURL, clone, shortUuid } from '@approot/utilities';
import { NextBusinessDayPayload } from '@approot/widgets/strategy-detail/leg/services/leg-date.service';
import {
  getAllAttributes,
  getAttributeControlFromKey
} from '@approot/widgets/strategy-detail/leg/utilities/leg-attribute-utilities';
import { BehaviorSubject, EMPTY, Observable } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { AlertsService } from '../alerts/alerts.service';
import { AttributeFormGeneratorService } from '../attribute-form-generator/attribute-form-generator.service';
import { AuthService, getHeaders } from '../auth/auth.service';
import { ConfigService } from '../config/config.service';
import { EntityMapperService } from '../entity-mapper/entity-mapper.service';
import { ProductsConfigService } from '../products-config/products-config.service';
import { TradesCaptureService } from '../trade-capture/trades-capture.service';

export interface GetImpliedVolPayload {
  task: 'getImpliedVol';
  currencyPair: string;
  cutOffOrFixingCode: string;
  datesAndStrikes: { date: string; strike: string }[];
  expirationDate: string;
  timestamp: string;
  marketDataSpreadingRule?: MarketDataSpreadingRule;
}

export interface GetForwardPointsPayload {
  task: 'getForwardPoints';
  currencyPair: string;
  expirationDates: string[];
  timestamp: string;
}

export interface SetFxFixingsPayload {
  task: 'setFxFixings';
  items: {
    currencyPair: string;
    date: string;
    code: string;
    spot: number;
  }[];
}

export interface SetIRFixingsPayload {
  task: 'setIrFixings';
  fixings: IRFixing[];
}

export interface GetFixingsForCurrencyPairAndCode {
  task: 'getFixingsForCurrencyPairAndCode';
  currencyPair: string;
  code: string;
}

interface NewLegData {
  productName: string;
  newProduct: boolean;
  preSetPosition: Record<string, any>;
  tradeViewMode: boolean;
  productSpec?: ProductSpecification;
  legBeingCopied?: Leg;
}

type ProductsObject = { product: KeyValue; attributes: LegAttribute[] }[];

const defaultLegValues = {
  checked: true,
  legDescription: {
    description: [],
    valueChanged: false
  },
  shortcutValueUsed: {
    strike: null,
    strike1: null,
    strike2: null
  },
  previousValuesOnFocus: {
    strike: '',
    strike1: '',
    strike2: ''
  },
  usingLatestValues: {},
  copiedAttributesData: {},
  manuallyEnteredForwardPointsAsk: false,
  manuallyEnteredForwardPointsBid: false,
  manuallyEnteredSpot: false,
  extraScheduleData: {},
  lastEditedDcdAttribute: '',
  missingAttributeOnPricing: '',
  depositCurrency: '',
  isMidToggled: false,
  position: '',
  priceHistory: [],
  baseCurrency: '',
  quoteCurrency: '',
  alternativeCurrency: '',
  usableCross: '',
  pricingErrorMsg: null,
  scheduleOpened: '',
  scheduleColumnNames: null,
  showBasket: false,
  showOptionsPortfolio: false,
  showOptionsPortfolioSpots: false,
  pricingData: {
    treasuryRates: [],
    fixings: [],
    marketData: []
  }
};

@Injectable({
  providedIn: 'root'
})
export class LegService {
  public attributesDisplayList: LegAttribute[];
  public productSpecifications: ProductSpecification[];
  public tradeAttributes: LegAttribute[] = [];
  public legs: Leg[] = [];
  pricingResultsExpanded = true;
  marketDataExpanded = true;
  markUpExpanded = true;
  dcdResultsExpanded = true;
  public activeAttributes = signal<LegAttribute[]>([]);
  activeExtraAttributes: any[] = [];
  activeDcdAttributes: any[] = [];
  private entityMapperService = inject(EntityMapperService);
  protected phxService = inject(PhoenixService);
  public acrossLegsValueChange = new BehaviorSubject<{
    key: string;
    value: string;
    sourceLegIndex: number;
    textColor: string;
  }>({ key: '', value: '', sourceLegIndex: null, textColor: '' });

  public setAcrossLegsValueChange(newValue: {
    key: string;
    value: string;
    sourceLegIndex: number;
    textColor: string;
  }): void {
    this.acrossLegsValueChange.next(newValue);
  }

  constructor(
    private attributeFormGeneratorService: AttributeFormGeneratorService,
    private alertsService: AlertsService,
    private http: HttpClient,
    private tradesCaptureService: TradesCaptureService,
    private authService: AuthService,
    private productsConfigService: ProductsConfigService,
    private configService: ConfigService
  ) {}

  // productName passed in here needs to be the long name in lowercase
  public addLeg(newLegData: NewLegData): void {
    // isNewProduct indicates whether the product has just been created or was already existent,
    // e.g. loaded trade, copied leg etc
    newLegData.productSpec =
      newLegData.productSpec || this.getProductSpec(newLegData.productName);
    if (newLegData.productSpec) {
      const leg = this.makeLeg(
        newLegData.productSpec,
        newLegData.preSetPosition,
        newLegData.newProduct,
        newLegData.tradeViewMode,
        newLegData.legBeingCopied
      );
      setAttributeDefaults(
        newLegData.preSetPosition,
        leg.attributesAsFormControls
      );
      this.legs.push(leg);
      this.updateActiveAttributes('add', leg, newLegData.tradeViewMode);
    } else {
      this.alertsService.addAlert({
        message: `Your account does not have access to the product: ${newLegData.productName}`,
        skipLog: true
      });
    }
  }

  public getProductSpec(
    productName: string,
    preSetPosition?: any
  ): ProductSpecification {
    if (!productName && preSetPosition?.productType) {
      productName = preSetPosition.productType;
    }

    return this.productSpecifications.find((product: ProductSpecification) => {
      return (
        product.productType.shortName === productName ||
        product.productType.longName.replace(/\s+/g, '').toLowerCase() ===
          productName
      );
    });
  }

  public makeLeg(
    productSpec: ProductSpecification,
    preSetPosition: any,
    newProduct: boolean,
    tradeViewMode: boolean,
    legBeingCopied?: Leg
  ): Leg {
    const {
      attributes,
      dcdResultsAttributes,
      productType,
      extraResultsAttributes,
      solver,
      scheduleAttributes,
      exerciseScheduleAttributes,
      floatingLegScheduleAttributes,
      fixedLegScheduleAttributes
    } = productSpec;

    const tradeAttributes = filterTradeAttributesForProduct(
      this.tradeAttributes,
      productType.shortName
    );

    const attributesAsFormControls = [
      ...this.attributeFormGeneratorService.convertAttributesIntoControls([
        ...attributes,
        ...tradeAttributes
      ])
    ];

    let defaultLegCreationValues = clone(defaultLegValues);

    if (legBeingCopied) {
      defaultLegCreationValues.usingLatestValues =
        legBeingCopied.usingLatestValues;

      defaultLegCreationValues.shortcutValueUsed =
        legBeingCopied.shortcutValueUsed;

      ['usingLatestValues', 'shortcutValueUsed'].forEach(defaultField => {
        Object.keys(defaultLegCreationValues[defaultField]).forEach(key => {
          if (!getAttributeControlFromKey(key, attributesAsFormControls)) {
            defaultLegCreationValues[defaultField][key] = null;
          }
        });
      });

      const oldLegAttributes = this.productsConfigService.getAllAttributes(
        legBeingCopied.attributesAsFormControls
      ) as AttributeBase<any>[];

      oldLegAttributes.forEach(attribute => {
        defaultLegCreationValues.copiedAttributesData[attribute.key] = {
          info: attribute.info,
          textColor: attribute.textColor
        };

        if (
          !tradeViewMode &&
          productType.shortName === 'newdcd' &&
          (attribute.key === 'depositRate' ||
            attribute.key === 'conversionRate' ||
            attribute.key === 'margin')
        ) {
          delete defaultLegCreationValues.copiedAttributesData[attribute.key]
            .textColor;
        }
      });
    }

    const leg = {
      attributesAsFormControls,
      solver,
      attributes: [...attributes, ...tradeAttributes],
      extraResultsAttributes,
      dcdResultsAttributes,
      productType: productType.shortName,
      preSetPosition,
      newProduct,
      tradeAttributes: tradeAttributes,
      scheduleAttributes,
      exerciseScheduleAttributes,
      floatingLegScheduleAttributes,
      fixedLegScheduleAttributes,
      isLegCopied: !!legBeingCopied,
      ...defaultLegCreationValues
    } as Leg;

    return leg;
  }

  public updateLegAttributes(leg: Leg): Leg {
    // required in the case that the backend has changed the spec of a product
    const productSpec = this.getProductSpec(
      leg.preSetPosition.productType.replace(/\s+/g, '').toLowerCase()
    );

    if (!productSpec) {
      return null;
    }

    const LegClone = clone(leg);
    const tradeAttributes = filterTradeAttributesForProduct(
      this.tradeAttributes,
      LegClone.productType
    );
    LegClone.attributes = clone([
      ...productSpec.attributes,
      ...tradeAttributes
    ]);
    LegClone.attributesAsFormControls = clone([
      ...this.attributeFormGeneratorService.convertAttributesIntoControls([
        ...productSpec.attributes,
        ...tradeAttributes
      ])
    ]);
    return LegClone;
  }

  createServicePayload(
    marketDataTimeStamp: string,
    marketDataSpreadingRule: MarketDataSpreadingRule,
    products: ProductsObject,
    task: Task,
    pricingData?: PricingData
  ) {
    let payload: Payload = {
      task: task,
      products: this.createProductsObject(products),
      timestamp: marketDataTimeStamp
    };
    if (pricingData) {
      payload.pricingData = pricingData;
      if (marketDataSpreadingRule) {
        payload.pricingData.marketDataSpreadingRule = marketDataSpreadingRule;
      }
    }
    return payload;
  }

  createProductsObject(products: ProductsObject): any[] {
    return products.map(x =>
      this.productsConfigService.productFormatter(x.product, x.attributes)
    );
  }

  priceLeg(payload: any): Observable<LegPrice | unknown> {
    payload = this.entityMapperService.cleanConfidentialFields(
      clone(payload),
      '/fx/pricing'
    );
    return this.authService.currentSession$().pipe(
      switchMap(cognitoToken =>
        this.http
          .post<LegPrice>(
            apiURL('/fx/pricing'),
            payload,
            this.getHeaders(cognitoToken.getIdToken().getJwtToken())
          )
          .pipe(
            tap(() => console.log('priced Leg')),
            catchError(err => {
              const id = this.logHydraError(err, '/fx/pricing', payload);
              return this.handleError('priceLeg error', id)(err);
            })
          )
      )
    );
  }

  public logHydraError(err: any, urlPath: string, payload: any) {
    const errorId = shortUuid();
    this.phxService.logEvent({
      errorId,
      type: 'hydra',
      category: 'error',
      urlPath,
      payload,
      error: err
    });
    return errorId;
  }

  public marketDataService(
    payload:
      | GetImpliedVolPayload
      | GetForwardPointsPayload
      | GetFixingsForCurrencyPairAndCode
      | SetFxFixingsPayload
      | SetIRFixingsPayload
  ): Observable<
    | GetImpliedVolResponse
    | GetForwardPointsResponse
    | GetFixingsForCurrencyPairAndCodeResponse
    | unknown
  > {
    return this.authService.currentSession$().pipe(
      switchMap(cognitoToken => {
        return this.http
          .post<GetImpliedVolResponse | GetForwardPointsResponse>(
            apiURL('/fx/marketDataService', { task: payload.task }),
            payload,
            getHeaders(cognitoToken.getIdToken().getJwtToken())
          )
          .pipe(
            catchError(err => {
              const id = this.logHydraError(
                err,
                '/fx/marketDataService',
                payload
              );
              return this.handleError('Market Data Service Error', id)(err);
            })
          );
      })
    );
  }

  dateService(payload: any): Observable<any> {
    return this.authService.currentSession$().pipe(
      switchMap(cognitoToken => {
        return this.http
          .post(
            apiURL('/fx/dateService'),
            payload,
            this.getHeaders(cognitoToken.getIdToken().getJwtToken())
          )
          .pipe(
            tap(() => console.log('date service date returned')),
            catchError(err => {
              const id = this.logHydraError(err, '/fx/dateService', payload);
              return this.handleError('date service error', id)(err);
            })
          );
      })
    );
  }

  // returns the future expiration and settlement dates
  public calculateFutureDate(
    payload: CalculateFutureDatePayload
  ): Promise<Observable<any>> {
    return this.authService.currentSession().then(cognitoToken => {
      return this.http
        .post(
          apiURL('/fx/expiration-settlement-dates'),
          payload,
          this.getHeaders(cognitoToken.getIdToken().getJwtToken())
        )
        .pipe(
          tap(() => console.log('calculated future date')),
          catchError(this.handleError('calculateFutureDate error', undefined))
        );
    });
  }

  public calculateExpirationDateFromSettlementDates(payload: {
    currencyPair: string;
    settlementDates: string | string[];
  }): Promise<Observable<any>> {
    return this.authService.currentSession().then(cognitoToken => {
      return this.http
        .post(
          apiURL('/fx/expiration-date-from-settlement-dates'),
          payload,
          this.getHeaders(cognitoToken.getIdToken().getJwtToken())
        )
        .pipe(
          catchError(
            this.handleError('expirationFromSettlementDate error', undefined)
          )
        );
    });
  }

  public nextBusinessDay(payload: NextBusinessDayPayload): Observable<any> {
    return this.authService.currentSession$().pipe(
      switchMap(cognitoToken => {
        return this.http
          .post(
            apiURL('/fx/next-business-day'),
            payload,
            this.getHeaders(cognitoToken.getIdToken().getJwtToken())
          )
          .pipe(
            tap(() => console.log('nextBusinessDay calculated')),
            catchError(this.handleError('nextBusinessDay error', undefined))
          );
      })
    );
  }

  public aggregateResults(payload: {
    results: ResultPerLeg[];
    midFlags: boolean[];
    net_vanilla_spreads: boolean;
    entity: string;
  }): Observable<{
    pricing: SummedPricing;
  }> {
    return this.authService
      .currentSession$()
      .pipe(
        switchMap(cognitoToken => {
          return this.http.post<AggregateResultsResponse>(
            apiURL('/fx/aggregateResults'),
            payload,
            this.getHeaders(cognitoToken.getIdToken().getJwtToken())
          );
        })
      )
      .pipe(map(x => x.response));
  }

  calculateSchedule(
    payload: SchedulePayload
  ): Observable<ScheduleData[] | unknown> {
    if (payload.frequency === Utilities.customText) {
      console.log('Skipping calculateSchedule for "Custom" frequency');
      return EMPTY;
    }
    return this.authService.currentSession$().pipe(
      switchMap(token => {
        return this.http
          .post<ScheduleData[]>(
            apiURL('/fx/schedule'),
            payload,
            this.getHeaders(token.getIdToken().getJwtToken())
          )
          .pipe(
            tap(() => console.log('calculateSchedule calculated')),
            catchError(this.handleError('calculateSchedule error', undefined))
          );
      })
    );
    // schedule solver based on providing start date, frequency, number of fixings (and cross )
  }

  // when we add or remove a leg, the list of active attributes on the left changes
  updateActiveAttributes(
    action: 'add' | 'remove' | 'update',
    leg: Partial<Leg>,
    tradeViewMode = false
  ): void {
    switch (action) {
      case 'add':
        // we need to merge the existing product attributes with the attributes of the newly added leg, and remove duplicates
        // we then also need to sort the attributes in the correct order, defined by productInfo.attributesDisplayList
        leg.attributes.forEach((element: LegAttribute) => {
          if (
            !this.activeAttributes().find(
              x => x.shortName === element.shortName
            )
          ) {
            const attribute = this.attributesDisplayList.find(y => {
              if (
                element.attributeType === 'tradeAttribute' &&
                y.shortName === element.shortName
              ) {
                return tradeViewMode;
              }
              return y.shortName === element.shortName;
            });

            const disableInTradeView =
              tradeViewMode && element.disableInTradeView;

            if (attribute && element.hide !== 'true' && !disableInTradeView) {
              this.activeAttributes.update(attributes => [
                ...attributes,
                attribute
              ]);
            }
          }
        });

        if (leg.extraResultsAttributes) {
          // places the extra result attributes into the array.
          leg.extraResultsAttributes.forEach(element => {
            if (
              !this.activeExtraAttributes.find(x => x.shortName === element)
            ) {
              const attr =
                this.productsConfigService.pricingResultsInfo.markUpSpecifications.find(
                  y => y.shortName === element
                );

              if (attr) {
                this.activeExtraAttributes.push(attr);
              }
            }
          });
          const sortingArrExtraResults =
            this.productsConfigService.pricingResultsInfo.markUpSpecifications.map(
              x => x.shortName
            );

          this.activeExtraAttributes.sort((a, b) => {
            return (
              sortingArrExtraResults.indexOf(a.shortName) -
              sortingArrExtraResults.indexOf(b.shortName)
            );
          });

          // allows treenode to detect a change
          this.activeExtraAttributes = clone(this.activeExtraAttributes);
        }
        if (leg.dcdResultsAttributes) {
          // places the extra result attributes into the array.
          leg.dcdResultsAttributes.forEach(element => {
            if (!this.activeDcdAttributes.find(x => x.shortName === element)) {
              const dcdResultAttribute =
                this.productsConfigService.pricingResultsInfo.dcdSpecifications.find(
                  y => y.shortName === element
                );

              if (dcdResultAttribute) {
                this.activeDcdAttributes.push(dcdResultAttribute);
              }
            }
          });
          const sortingArrDcdResults =
            this.productsConfigService.pricingResultsInfo.dcdSpecifications.map(
              x => x.shortName
            );

          this.activeDcdAttributes.sort((a, b) => {
            return (
              sortingArrDcdResults.indexOf(a.shortName) -
              sortingArrDcdResults.indexOf(b.shortName)
            );
          });

          this.activeDcdAttributes = [...this.activeDcdAttributes];
          // allows treenode to detect a change
        }

        this.sortActiveAttributes();
        break;
      case 'remove':
      case 'update':
        this.activeAttributes.set([]);
        this.activeExtraAttributes = [];
        this.activeDcdAttributes = [];
        this.legs.map(leg => {
          this.updateActiveAttributes('add', leg || {}, tradeViewMode);
        });
        break;
    }
  }

  sortActiveAttributes() {
    const sortingArr = this.attributesDisplayList.map(x => x.shortName);

    this.activeAttributes.set(
      clone(this.activeAttributes()).sort((a, b) => {
        return (
          sortingArr.indexOf(a.shortName) - sortingArr.indexOf(b.shortName)
        );
      })
    );
  }

  addTradeAttributes() {
    this.productsConfigService.productInfo.tradeAttributes.forEach(
      attribute => {
        this.activeAttributes.update(attributes => [
          ...attributes,
          this.attributesDisplayList.filter(
            attributeDisplay =>
              attributeDisplay.shortName === attribute.shortName
          )[0]
        ]);
      }
    );
    this.sortActiveAttributes();
  }

  public updateEntities(
    entities: Entity[],
    depositTakers?: string[]
  ): string[] {
    const identifiers = entities.map((entity: Entity) => entity.identifier);
    this.legs.forEach(leg => {
      // iterate through the legs and update the counter parties
      leg.attributesAsFormControls
        .filter(
          attribute =>
            attribute.key === 'counterPartyA' ||
            attribute.key === 'counterPartyB'
        )
        .forEach(counterParty => {
          counterParty.options = identifiers;
        });

      if (depositTakers) {
        const depositTakerControl = leg.attributesAsFormControls.find(
          attribute => attribute.key === 'depositTaker'
        );

        if (depositTakerControl) {
          depositTakerControl.options = depositTakers;
        }
      }
    });

    if (this.configService.userSettings().parent.getCounterpartiesFromBackEnd) {
      this.updateTradeViewCounterPartiesFromBackend('A');
      this.updateTradeViewCounterPartiesFromBackend('B');
    } else {
      this.updateTradeViewCounterParties(identifiers);
    }

    if (depositTakers) {
      const depositTakerAttribute =
        this.productsConfigService.productInfo.tradeAttributes.find(
          attribute => attribute.shortName === 'depositTaker'
        );

      if (depositTakerAttribute) {
        depositTakerAttribute.value = depositTakers;
      }
    }

    this.tradesCaptureService.entities = entities;
    return identifiers;
  }

  public updateTradeViewCounterPartiesFromBackend(side: 'A' | 'B') {
    const shortName = 'counterParty' + side;
    this.tradesCaptureService
      .getCounterPartyList(side)
      .subscribe(counterPartyList => {
        const counterParty =
          this.productsConfigService.productInfo.tradeAttributes.find(
            attribute => attribute.shortName === shortName
          );
        if (counterParty) {
          counterParty.value = [...counterPartyList, '(reset)'];
        }
      });
  }

  public handleError<T>(operation: string, errorId?: string) {
    return (error: HttpErrorResponse): Observable<T> => {
      console.error('handleError(leg)', error);
      let message: any;
      if (error.error instanceof ProgressEvent) {
        message =
          'Request has timed out and has been reported.  Please contact your sales representative if needed';
      } else {
        if (typeof error.error === 'string') {
          message = error.error;
        } else {
          message =
            error.error?.message || error.message || error.error || error;
        }
      }

      if (typeof message === 'string') {
        this.alertsService.addHttpAlert({ message, id: errorId, error: error });
      }

      raygun('send', { error: message, tags: [operation] });
      throw new Error(`${operation} failed: ${message}`); // I think this is useless.
    };
  }

  public spreadAttributeValue(
    key: string,
    value: string,
    sourceLegIndex: number,
    textColor: string
  ): void {
    this.setAcrossLegsValueChange({ key, value, sourceLegIndex, textColor });
  }

  private updateTradeViewCounterParties(identifiers: string[]): void {
    this.productsConfigService.productInfo.tradeAttributes
      .filter(
        attribute =>
          attribute.shortName === 'counterPartyA' ||
          attribute.shortName === 'counterPartyB'
      )
      .forEach(counterParty => {
        counterParty.value = identifiers;
      });
  }

  private getHeaders(token) {
    let headers = new HttpHeaders()
      .set('Content-Type', 'application/json')
      .set('Authorization', token);
    return { headers };
  }
}

function setAttributeDefaults(
  preSetPosition: Record<string, any>,
  attributesAsFormControls?: AttributeBase<any>[]
): void {
  if (preSetPosition) {
    getAllAttributes(attributesAsFormControls).forEach(attribute => {
      const key = attribute.key;
      if (preSetPosition[key] == null) {
        preSetPosition[key] = attribute.default;
      }
    });
  }
}

function filterTradeAttributesForProduct(
  tradeAttributes: LegAttribute[],
  productType: string
): LegAttribute[] {
  return tradeAttributes.filter(
    attribute =>
      !attribute.productTypes || attribute.productTypes.includes(productType)
  );
}
